
import openPreviewPage from "../previewSchedulePage/actions/openPreviewPage";
import { previewSchedulePageStore } from "../previewSchedulePage";
import * as moment from "moment";

export enum GenerateOptions {
    REPLACE = "replace",
    CONSIDER = "consider"
}

/**
 * Shared Smart Schedule helper utilities.
 * handleReviewGeneratedSchedule now accepts generateOption derived from job status (overrideExistingSchedule).
 * generateOption values: 'replace' | 'consider'.
 */
export function handleReviewGeneratedSchedule(
    tagId: string,
    startDate: string,
    endDate: string,
    jobId: string,
    generateOption?: GenerateOptions,
    onNavigate?: () => void
): void {
    openPreviewPage(previewSchedulePageStore(), true, tagId, startDate, endDate, jobId, generateOption);
}

/** Map boolean overrideExistingSchedule -> generateOption string */
export function mapOverrideToGenerateOption(overrideExistingSchedule?: boolean): GenerateOptions | undefined {
    if (overrideExistingSchedule === undefined) {
        return undefined;
    }
    return overrideExistingSchedule ? GenerateOptions.REPLACE : GenerateOptions.CONSIDER;
}

/**
 * Compute UTC datetime boundaries for schedule generation.
 * Combines user's selected start date with earliest shift block start time (UTC),
 * and user's selected end date with latest shift block end time (UTC).
 * If shiftBlocks is empty, falls back to start of startDate and end of endDate (UTC).
 */
export function computeSmartScheduleBoundaries(
    shiftBlocks: Array<{ startTime: any; endTime: any }> | undefined,
    userStartDate: any,
    userEndDate: any
): { startDateBoundaryUtc: string; endDateBoundaryUtc: string } {
    if (shiftBlocks && shiftBlocks.length > 0) {
        // Parse each block preserving zone info
        const startMoments = shiftBlocks.map(sb => moment.parseZone(sb.startTime));
        const endMomentsOriginal = shiftBlocks.map(sb => moment.parseZone(sb.endTime));

        // Helper to pick min/max by timestamp value
        const pickByTimestamp = (arr: moment.Moment[], comparator: (a: number, b: number) => boolean) => {
            return arr.reduce((acc, cur) => (comparator(cur.valueOf(), acc.valueOf()) ? cur : acc));
        };
        const earliestStartLocal = pickByTimestamp(startMoments, (a, b) => a < b);

        // Rebase end times onto userEndDate's date part. If an end spans into next day relative to its start (overnight), add +1 day.
        const userEndDateBase = moment(userEndDate).startOf('day');
        const rebasedEndMoments = endMomentsOriginal.map((endMoment, idx) => {
            const startMoment = startMoments[idx];
            const rebased = userEndDateBase.clone().set({
                hour: endMoment.hour(),
                minute: endMoment.minute(),
                second: endMoment.second(),
                millisecond: 0
            });
            // If original end crosses midnight (original end calendar date > original start OR end time-of-day < start time-of-day), treat as next day
            if (endMoment.isAfter(startMoment, 'day') || endMoment.hour() < startMoment.hour() || (endMoment.hour() === startMoment.hour() && endMoment.minute() < startMoment.minute())) {
                rebased.add(1, 'day');
            }
            return rebased;
        });
        const latestEndLocal = pickByTimestamp(rebasedEndMoments, (a, b) => a > b);

        // Combine start using userStartDate date part and earliest start time
        const userStartDateLocal = moment(userStartDate).startOf('day');
        const combinedStart = userStartDateLocal.clone().set({
            hour: earliestStartLocal.hour(),
            minute: earliestStartLocal.minute(),
            second: earliestStartLocal.second(),
            millisecond: 0
        }).utc();

        const combinedEnd = latestEndLocal.clone().utc();

        return {
            startDateBoundaryUtc: combinedStart.toISOString(),
            endDateBoundaryUtc: combinedEnd.toISOString()
        };
    }
    // Fallback: no shift blocks; use start/end of day after combining then convert
    const startFallback = moment(userStartDate).startOf('day').utc().toISOString();
    const endFallback = moment(userEndDate).endOf('day').utc().toISOString();
    return { startDateBoundaryUtc: startFallback, endDateBoundaryUtc: endFallback };
}

export default {
    handleReviewGeneratedSchedule,
    mapOverrideToGenerateOption,
    computeSmartScheduleBoundaries
};